import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Check, CalendarDays, MapPin, Phone, Mail, CreditCard, FileText, HandCoins, ArrowLeft, ArrowRight, Sprout, Info } from "lucide-react";
import { toast } from "sonner";
import { SkipToContent } from "@/components/site/SkipToContent";
import { StickyHeader } from "@/components/site/StickyHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageHero } from "@/components/site/PageHero";
import { Reveal, Kicker, WfButton, ImageWithCredit } from "@/components/site/primitives";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  CSA_HERO,
  CSA_SEASON,
  CSA_CONTACT,
  CSA_SHARE_OPTIONS,
  CSA_PICKUP_LOCATIONS,
  CSA_SEASONS,
  CSA_CULTURAL_NOTE,
  CSA_PAYMENT_METHODS,
  CSA_SNAP_INFO,
  CSA_FAQS,
} from "@/content/csa";
import { scrollToId } from "@/lib/scrollTo";
import { cn } from "@/lib/utils";

const PAY_ICON = { card: CreditCard, check: FileText, snap: HandCoins };
const PROCESSING_FEE_RATE = 0.03; // illustrative card processing fee for the demo checkout

/* ---------------- Quick links (anchor down) ---------------- */
const QUICK_LINKS = [
  { id: "snap-hip", label: "Learn about our SNAP CSA", icon: HandCoins, testid: "csa-quicklink-snap" },
  { id: "faqs", label: "CSA FAQs", icon: Info, testid: "csa-quicklink-faq" },
  { id: "signup", label: "Sign up today", icon: Check, testid: "csa-quicklink-signup" },
];
const QuickLinks = () => (
  <nav aria-label="CSA quick links" className="bg-[var(--wf-maroon)]">
    <div className="wf-container flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm font-semibold text-[rgba(255,253,248,0.9)]">Looking for payment options, eligibility, or answers?</p>
      <ul className="flex flex-wrap gap-2">
        {QUICK_LINKS.map((l) => (
          <li key={l.id}>
            <a
              href={`#${l.id}`}
              onClick={(e) => { e.preventDefault(); scrollToId(l.id); }}
              data-testid={l.testid}
              className="inline-flex min-h-11 items-center gap-2 border border-white/40 bg-white/10 px-4 text-sm font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <l.icon className="h-4 w-4" aria-hidden="true" />
              {l.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  </nav>
);

/* ---------------- Season strip ---------------- */
const SeasonStrip = () => (
  <section aria-label="Season at a glance" className="border-b border-[var(--wf-border)] bg-[var(--wf-paper)]">
    <div className="wf-container grid grid-cols-1 divide-y divide-[var(--wf-border)] sm:grid-cols-3 sm:divide-x sm:divide-y-0">
      {[
        { icon: CalendarDays, label: "Pick-up day", value: CSA_SEASON.cadence },
        { icon: Sprout, label: "Season length", value: CSA_SEASON.weeks },
        { icon: MapPin, label: "Dates", value: CSA_SEASON.dates },
      ].map((s) => (
        <div key={s.label} className="flex items-center gap-4 py-5 sm:px-6 first:sm:pl-0">
          <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[rgba(107,31,43,0.10)] text-[var(--wf-maroon)]"><s.icon className="h-5 w-5" aria-hidden="true" /></span>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[var(--wf-green-text)]">{s.label}</p>
            <p className="font-serif text-lg font-semibold text-[var(--wf-ink)]">{s.value}</p>
          </div>
        </div>
      ))}
    </div>
  </section>
);

/* ---------------- Shares ---------------- */
const SharesSection = ({ onChoose }) => (
  <section id="shares" aria-labelledby="csa-shares-heading" className="wf-section scroll-mt-20 bg-[var(--wf-cream)]">
    <div className="wf-container">
      <Reveal className="mx-auto max-w-2xl text-center">
        <Kicker className="justify-center">Choose your share</Kicker>
        <h2 id="csa-shares-heading" className="mt-4 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl lg:text-5xl">Two share sizes, one great season</h2>
        <p className="mt-4 text-base leading-7 text-muted-foreground">Every share includes seasonal leafy greens, root vegetables, fruiting vegetables, herbs — and a featured cultural crop with a recipe card.</p>
      </Reveal>
      <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {CSA_SHARE_OPTIONS.map((s, i) => (
          <Reveal key={s.id} delay={0.07 * i}>
            <div data-testid={`csa-share-card-${s.id}`} className={cn("relative flex h-full flex-col overflow-hidden rounded-sm border bg-[var(--wf-paper)] shadow-[var(--wf-shadow-sm)] lg:flex-row", s.featured ? "border-[var(--wf-maroon)]" : "border-[var(--wf-border)]")}>
              <div className="relative aspect-[16/10] lg:aspect-auto lg:w-2/5">
                <img src={s.image} alt={s.imageAlt} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
                {s.featured && <span className="absolute left-4 top-4 rounded-full bg-[var(--wf-maroon)] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--wf-paper)]">Most popular</span>}
              </div>
              <div className="flex flex-1 flex-col p-7">
                <h3 className="font-serif text-2xl font-semibold text-[var(--wf-ink)]">{s.name}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{s.feeds}</p>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="font-serif text-4xl font-semibold text-[var(--wf-maroon)]">{s.priceLabel}</span>
                  <span className="text-sm text-muted-foreground">{s.seasonLabel}</span>
                </div>
                {s.installments && <p className="mt-1 text-xs font-medium text-[var(--wf-green-text)]">Option to pay in two installments</p>}
                <p className="mt-4 flex-1 text-sm leading-6 text-[var(--wf-soil)]">{s.description}</p>
                <ul className="mt-4 space-y-2 text-sm text-[var(--wf-ink)]">
                  <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--wf-green-text)]" aria-hidden="true" />{s.items} seasonal items every week</li>
                  <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--wf-green-text)]" aria-hidden="true" />Featured cultural crop + recipe card</li>
                  <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[var(--wf-green-text)]" aria-hidden="true" />SNAP/HIP: ${s.snapMonthly}/month for 4 months</li>
                </ul>
                <WfButton type="button" onClick={() => onChoose(s.id)} withArrow variant={s.featured ? "primary" : "secondary"} className="mt-6 rounded-full" data-testid={`csa-choose-${s.id}`}>
                  Choose {s.name}
                </WfButton>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      <p className="mt-6 text-center text-sm text-[var(--wf-soil)]">{CSA_SEASON.note}</p>
    </div>
  </section>
);

/* ---------------- Seasons ---------------- */
const SeasonsSection = () => (
  <section id="through-the-seasons" aria-labelledby="csa-seasons-heading" className="wf-section bg-white">
    <div className="wf-container">
      <Reveal className="max-w-2xl">
        <Kicker>What lands in your box</Kicker>
        <h2 id="csa-seasons-heading" className="mt-4 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl lg:text-5xl">Our Shares Through the Seasons</h2>
      </Reveal>
      <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
        {CSA_SEASONS.map((s, i) => (
          <Reveal key={s.id} delay={0.07 * i}>
            <div data-testid={`csa-season-${s.id}`} className="flex h-full flex-col overflow-hidden rounded-sm border border-[var(--wf-border)] bg-[var(--wf-paper)] shadow-[var(--wf-shadow-sm)]">
              <ImageWithCredit src={s.image} alt={s.imageAlt} className="aspect-[16/10]" imgClassName="object-cover" />
              <div className="p-6">
                <h3 className="font-serif text-xl font-semibold text-[var(--wf-ink)]">{s.label}</h3>
                <ul className="mt-3 flex flex-wrap gap-2">
                  {s.crops.map((c) => (<li key={c} className="rounded-full bg-[var(--wf-wheat)] px-3 py-1 text-xs font-medium text-[var(--wf-maroon)]">{c}</li>))}
                </ul>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.1}>
        <div className="mt-8 flex items-start gap-4 rounded-sm border border-[var(--wf-border)] bg-[var(--wf-wheat)]/60 p-6">
          <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--wf-maroon)] text-white"><Sprout className="h-5 w-5" aria-hidden="true" /></span>
          <div>
            <p className="text-sm leading-7 text-[var(--wf-ink)] sm:text-base">{CSA_CULTURAL_NOTE}</p>
            <Link to="/crops" data-testid="csa-crops-link" className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-[var(--wf-maroon)] underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm">Explore our crops <ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------------- Pickup locations ---------------- */
const PickupSection = () => (
  <section id="pickup-locations" aria-labelledby="csa-pickup-heading" className="wf-section bg-white">
    <div className="wf-container">
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1.4fr] lg:gap-16">
        <Reveal>
          <Kicker>Pickup locations</Kicker>
          <h2 id="csa-pickup-heading" className="mt-4 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl">Every Thursday, close to home</h2>
          <p className="mt-4 text-base leading-7 text-[var(--wf-soil)]">World Farmers offers multiple CSA pick-up locations across the region for your convenience.</p>
          <p className="mt-4 rounded-sm border border-[var(--wf-border)] bg-[var(--wf-paper)] p-4 text-sm leading-6 text-[var(--wf-soil)]">{CSA_CONTACT.hostNote}</p>
        </Reveal>
        <Reveal delay={0.1}>
          <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {CSA_PICKUP_LOCATIONS.map((l) => (
              <li key={l.id} data-testid={`csa-pickup-${l.id}`} className="rounded-sm border border-[var(--wf-border)] bg-[var(--wf-paper)] p-5 shadow-[var(--wf-shadow-sm)]">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[rgba(47,93,58,0.12)] text-[var(--wf-green-text)]"><MapPin className="h-4 w-4" aria-hidden="true" /></span>
                <h3 className="mt-3 font-serif text-lg font-semibold text-[var(--wf-ink)]">{l.name}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{l.address}</p>
                <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(l.address)}`} target="_blank" rel="noopener noreferrer" className="mt-3 inline-block text-xs font-semibold text-[var(--wf-maroon)] underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm">Get directions</a>
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ---------------- Sign-up (DUMMY CHECKOUT) ---------------- */
const STEPS = ["Share", "Pickup", "Payment", "Details", "Confirm"];

const CsaSignup = ({ shareId, setShareId }) => {
  const [step, setStep] = useState(0);
  const [pickup, setPickup] = useState("");
  const [payment, setPayment] = useState("");
  const [installments, setInstallments] = useState(false);
  const [details, setDetails] = useState({ name: "", email: "", phone: "", household: "", snapAmount: "", card: "", exp: "", cvc: "" });
  const [agree, setAgree] = useState(false);
  const [errors, setErrors] = useState({});
  const [done, setDone] = useState(false);

  const share = CSA_SHARE_OPTIONS.find((s) => s.id === shareId);
  const location = CSA_PICKUP_LOCATIONS.find((l) => l.id === pickup);
  const method = CSA_PAYMENT_METHODS.find((m) => m.id === payment);

  const totals = useMemo(() => {
    if (!share) return null;
    if (payment === "snap") return { label: `$${share.snapMonthly}/month × 4 months`, total: share.snapMonthly * 4, fee: 0, due: share.snapMonthly };
    const fee = payment === "card" ? Math.round(share.price * PROCESSING_FEE_RATE) : 0;
    const total = share.price + fee;
    const due = installments ? Math.ceil(total / 2) : total;
    return { label: installments ? "2 installments" : "Pay in full", total, fee, due };
  }, [share, payment, installments]);

  const set = (k) => (e) => setDetails((d) => ({ ...d, [k]: e.target.value }));

  const validateStep = () => {
    const next = {};
    if (step === 0 && !share) next.share = "Please choose a share.";
    if (step === 1 && !pickup) next.pickup = "Please choose a pickup location.";
    if (step === 2 && !payment) next.payment = "Please choose a payment method.";
    if (step === 3) {
      if (!details.name.trim()) next.name = "Please enter your name.";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(details.email.trim())) next.email = "Please enter a valid email address.";
      if (!details.phone.trim()) next.phone = "Please enter a phone number.";
      if (payment === "card") {
        if (details.card.replace(/\s/g, "").length < 12) next.card = "Enter a card number (demo — any 12+ digits).";
        if (!details.exp.trim()) next.exp = "Enter an expiration date.";
        if (details.cvc.trim().length < 3) next.cvc = "Enter the security code.";
      }
    }
    if (step === 4 && !agree) next.agree = "Please confirm to continue.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const nextStep = () => { if (validateStep()) { setStep((s) => Math.min(s + 1, STEPS.length - 1)); scrollToId("signup"); } };
  const prevStep = () => { setErrors({}); setStep((s) => Math.max(s - 1, 0)); scrollToId("signup"); };

  const confirm = () => {
    if (!validateStep()) return;
    setDone(true);
    toast.success("You’re signed up! (Demo)", { description: "No payment was processed — this checkout is a preview until a payment provider is chosen." });
    scrollToId("signup");
  };

  const reset = () => { setStep(0); setShareId(""); setPickup(""); setPayment(""); setInstallments(false); setDetails({ name: "", email: "", phone: "", household: "", snapAmount: "", card: "", exp: "", cvc: "" }); setAgree(false); setErrors({}); setDone(false); };

  const field = "min-h-11 rounded-lg border-[var(--wf-border)] bg-white focus-visible:ring-[var(--wf-maroon)]";
  const err = (k) => errors[k] && <p id={`csa-${k}-error`} role="alert" data-testid={`csa-${k}-error`} className="mt-1 text-sm text-[var(--wf-maroon)]">{errors[k]}</p>;

  const OptionCard = ({ selected, onClick, testid, children, className }) => (
    <button type="button" onClick={onClick} aria-pressed={selected} data-testid={testid} className={cn("flex w-full items-start gap-4 rounded-sm border p-5 text-left transition-[border-color,box-shadow] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background", selected ? "border-[var(--wf-maroon)] bg-[var(--wf-wheat)]/50 shadow-[var(--wf-shadow-sm)]" : "border-[var(--wf-border)] bg-[var(--wf-paper)] hover:border-[var(--wf-maroon)]/50", className)}>
      <span aria-hidden="true" className={cn("mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2", selected ? "border-[var(--wf-maroon)] bg-[var(--wf-maroon)] text-white" : "border-[var(--wf-border)] bg-white")}>{selected && <Check className="h-3 w-3" />}</span>
      <span className="min-w-0 flex-1">{children}</span>
    </button>
  );

  return (
    <section id="signup" aria-labelledby="csa-signup-heading" className="wf-section scroll-mt-20 bg-[var(--wf-cream)]">
      <div className="wf-container">
        <Reveal className="mx-auto max-w-2xl text-center">
          <Kicker className="justify-center">Sign up today</Kicker>
          <h2 id="csa-signup-heading" className="mt-4 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl lg:text-5xl">Reserve your 2026 share</h2>
          <p className="mt-4 text-base leading-7 text-muted-foreground">We accept check and online payment, or SNAP/EBT. Select how you would like to pay and we’ll guide you from there.</p>
        </Reveal>

        <div className="mx-auto mt-10 max-w-3xl rounded-sm border border-[var(--wf-border)] bg-[var(--wf-cream)] p-5 shadow-[var(--wf-shadow-sm)] sm:p-8" data-testid="csa-checkout">
          {/* Stepper */}
          <ol className="flex items-center gap-2" aria-label="Sign-up progress">
            {STEPS.map((label, i) => (
              <li key={label} className="flex flex-1 items-center gap-2" aria-current={i === step ? "step" : undefined}>
                <span className={cn("inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold", i < step ? "bg-[var(--wf-farm-green)] text-white" : i === step ? "bg-[var(--wf-maroon)] text-white" : "bg-[var(--wf-wheat)] text-[var(--wf-soil)]")} data-testid={`csa-step-${i}`}>
                  {i < step ? <Check className="h-4 w-4" aria-hidden="true" /> : i + 1}
                </span>
                <span className={cn("hidden text-xs font-semibold sm:block", i === step ? "text-[var(--wf-ink)]" : "text-muted-foreground")}>{label}</span>
                {i < STEPS.length - 1 && <span aria-hidden="true" className={cn("h-px flex-1", i < step ? "bg-[var(--wf-farm-green)]" : "bg-[var(--wf-border)]")} />}
              </li>
            ))}
          </ol>

          <div className="mt-8" data-testid={`csa-step-panel-${done ? "done" : step}`}>
            {done ? (
              <div role="status" data-testid="csa-success" className="text-center">
                <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[var(--wf-farm-green)] text-white"><Check className="h-7 w-7" aria-hidden="true" /></span>
                <h3 className="mt-4 font-serif text-2xl font-semibold text-[var(--wf-ink)]">Welcome to the World Farmers CSA, {details.name.split(" ")[0]}!</h3>
                <p className="mx-auto mt-3 max-w-md text-base leading-7 text-[var(--wf-soil)]">
                  {payment === "snap" && "We’ll reach out shortly to complete your DTA contract and confirm your first pickup."}
                  {payment === "check" && "Please mail your check to World Farmers, P.O. Box 112, Lancaster, MA 01523. We’ll email a confirmation when it arrives."}
                  {payment === "card" && "A confirmation email is on its way with your pickup details."}
                </p>
                <dl className="mx-auto mt-6 grid max-w-md grid-cols-2 gap-3 text-left text-sm">
                  <div className="rounded-sm bg-[var(--wf-paper)] p-3"><dt className="text-xs uppercase tracking-[0.1em] text-muted-foreground">Share</dt><dd className="font-semibold text-[var(--wf-ink)]">{share?.name}</dd></div>
                  <div className="rounded-sm bg-[var(--wf-paper)] p-3"><dt className="text-xs uppercase tracking-[0.1em] text-muted-foreground">Pickup</dt><dd className="font-semibold text-[var(--wf-ink)]">{location?.town}, Thursdays</dd></div>
                </dl>
                <p className="mt-6 inline-flex items-center gap-2 rounded-full bg-[var(--wf-wheat)] px-4 py-2 text-xs font-semibold text-[var(--wf-maroon)]"><Info className="h-4 w-4" aria-hidden="true" /> Demo checkout — no payment was processed.</p>
                <div className="mt-6"><button type="button" onClick={reset} data-testid="csa-reset" className="text-sm font-semibold text-[var(--wf-maroon)] underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm">Start another sign-up</button></div>
              </div>
            ) : (
              <>
                {/* Step 0: Share */}
                {step === 0 && (
                  <fieldset>
                    <legend className="font-serif text-xl font-semibold text-[var(--wf-ink)]">Which share is right for you?</legend>
                    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {CSA_SHARE_OPTIONS.map((s) => (
                        <OptionCard key={s.id} selected={shareId === s.id} onClick={() => setShareId(s.id)} testid={`csa-option-share-${s.id}`}>
                          <span className="block font-serif text-lg font-semibold text-[var(--wf-ink)]">{s.name}</span>
                          <span className="block text-sm text-muted-foreground">{s.feeds} · {s.items} items/week</span>
                          <span className="mt-2 block font-serif text-2xl font-semibold text-[var(--wf-maroon)]">{s.priceLabel} <span className="font-sans text-xs font-medium text-muted-foreground">/ season</span></span>
                        </OptionCard>
                      ))}
                    </div>
                    {err("share")}
                  </fieldset>
                )}

                {/* Step 1: Pickup */}
                {step === 1 && (
                  <fieldset>
                    <legend className="font-serif text-xl font-semibold text-[var(--wf-ink)]">Where would you like to pick up?</legend>
                    <p className="mt-1 text-sm text-muted-foreground">Pickups are every Thursday, July 9 – October 22.</p>
                    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                      {CSA_PICKUP_LOCATIONS.map((l) => (
                        <OptionCard key={l.id} selected={pickup === l.id} onClick={() => setPickup(l.id)} testid={`csa-option-pickup-${l.id}`}>
                          <span className="block font-semibold text-[var(--wf-ink)]">{l.name}</span>
                          <span className="block text-sm text-muted-foreground">{l.address}</span>
                        </OptionCard>
                      ))}
                    </div>
                    {err("pickup")}
                  </fieldset>
                )}

                {/* Step 2: Payment */}
                {step === 2 && (
                  <fieldset>
                    <legend className="font-serif text-xl font-semibold text-[var(--wf-ink)]">How would you like to pay?</legend>
                    <div className="mt-4 grid grid-cols-1 gap-3">
                      {CSA_PAYMENT_METHODS.map((m) => {
                        const Icon = PAY_ICON[m.id];
                        return (
                          <OptionCard key={m.id} selected={payment === m.id} onClick={() => { setPayment(m.id); if (m.id === "snap") setInstallments(false); }} testid={`csa-option-payment-${m.id}`}>
                            <span className="flex items-center gap-2 font-semibold text-[var(--wf-ink)]"><Icon className="h-4 w-4 text-[var(--wf-maroon)]" aria-hidden="true" />{m.label} <span className="text-xs font-medium text-muted-foreground">— {m.short}</span></span>
                            <span className="mt-1 block text-sm leading-6 text-[var(--wf-soil)]">{m.detail}</span>
                            {m.id === "snap" && share && <span className="mt-2 block text-sm font-semibold text-[var(--wf-green-text)]">{share.name}: ${share.snapMonthly}/month for July–October</span>}
                          </OptionCard>
                        );
                      })}
                    </div>
                    {err("payment")}
                    {(payment === "card" || payment === "check") && share?.installments && (
                      <div className="mt-4 flex items-center gap-2 rounded-sm border border-[var(--wf-border)] bg-[var(--wf-paper)] p-4">
                        <Checkbox id="csa-installments" checked={installments} onCheckedChange={(v) => setInstallments(Boolean(v))} data-testid="csa-installments-checkbox" className="border-[var(--wf-border)] data-[state=checked]:bg-[var(--wf-maroon)] data-[state=checked]:border-[var(--wf-maroon)]" />
                        <Label htmlFor="csa-installments" className="text-sm font-medium text-[var(--wf-ink)]">Pay in two installments (half now, half by August 1)</Label>
                      </div>
                    )}
                    {payment === "snap" && (
                      <p className="mt-4 rounded-sm border border-[var(--wf-border)] bg-[var(--wf-paper)] p-4 text-sm leading-6 text-[var(--wf-soil)]">{share?.snapNote}</p>
                    )}
                  </fieldset>
                )}

                {/* Step 3: Details */}
                {step === 3 && (
                  <fieldset>
                    <legend className="font-serif text-xl font-semibold text-[var(--wf-ink)]">Your details</legend>
                    <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div><Label htmlFor="csa-name" className="text-sm font-semibold text-[var(--wf-ink)]">Full name</Label><Input id="csa-name" value={details.name} onChange={set("name")} autoComplete="name" aria-invalid={!!errors.name} data-testid="csa-name-input" className={cn(field, "mt-1.5")} />{err("name")}</div>
                      <div><Label htmlFor="csa-email" className="text-sm font-semibold text-[var(--wf-ink)]">Email</Label><Input id="csa-email" type="email" inputMode="email" autoComplete="email" value={details.email} onChange={set("email")} aria-invalid={!!errors.email} data-testid="csa-email-input" className={cn(field, "mt-1.5")} />{err("email")}</div>
                      <div><Label htmlFor="csa-phone" className="text-sm font-semibold text-[var(--wf-ink)]">Phone</Label><Input id="csa-phone" type="tel" inputMode="tel" autoComplete="tel" value={details.phone} onChange={set("phone")} aria-invalid={!!errors.phone} data-testid="csa-phone-input" className={cn(field, "mt-1.5")} />{err("phone")}</div>
                      <div><Label htmlFor="csa-household" className="text-sm font-semibold text-[var(--wf-ink)]">Household size <span className="font-normal text-muted-foreground">(optional)</span></Label><Input id="csa-household" inputMode="numeric" value={details.household} onChange={set("household")} data-testid="csa-household-input" className={cn(field, "mt-1.5")} /></div>
                      {payment === "snap" && (
                        <div className="sm:col-span-2"><Label htmlFor="csa-snap-amount" className="text-sm font-semibold text-[var(--wf-ink)]">Approximate monthly SNAP benefit <span className="font-normal text-muted-foreground">(optional, helps us recommend a share)</span></Label><Input id="csa-snap-amount" inputMode="numeric" placeholder="$" value={details.snapAmount} onChange={set("snapAmount")} data-testid="csa-snap-amount-input" className={cn(field, "mt-1.5")} /></div>
                      )}
                      {payment === "card" && (
                        <>
                          <div className="sm:col-span-2"><Label htmlFor="csa-card" className="text-sm font-semibold text-[var(--wf-ink)]">Card number</Label><Input id="csa-card" inputMode="numeric" autoComplete="cc-number" placeholder="4242 4242 4242 4242" value={details.card} onChange={set("card")} aria-invalid={!!errors.card} data-testid="csa-card-input" className={cn(field, "mt-1.5")} />{err("card")}</div>
                          <div><Label htmlFor="csa-exp" className="text-sm font-semibold text-[var(--wf-ink)]">Expiration</Label><Input id="csa-exp" placeholder="MM / YY" autoComplete="cc-exp" value={details.exp} onChange={set("exp")} aria-invalid={!!errors.exp} data-testid="csa-exp-input" className={cn(field, "mt-1.5")} />{err("exp")}</div>
                          <div><Label htmlFor="csa-cvc" className="text-sm font-semibold text-[var(--wf-ink)]">Security code</Label><Input id="csa-cvc" inputMode="numeric" autoComplete="cc-csc" placeholder="123" value={details.cvc} onChange={set("cvc")} aria-invalid={!!errors.cvc} data-testid="csa-cvc-input" className={cn(field, "mt-1.5")} />{err("cvc")}</div>
                          <p className="text-xs text-muted-foreground sm:col-span-2">Demo checkout — card details are not stored or charged.</p>
                        </>
                      )}
                    </div>
                  </fieldset>
                )}

                {/* Step 4: Confirm */}
                {step === 4 && share && totals && (
                  <div>
                    <h3 className="font-serif text-xl font-semibold text-[var(--wf-ink)]">Review your share</h3>
                    <dl className="mt-4 divide-y divide-[var(--wf-border)] rounded-sm border border-[var(--wf-border)] bg-[var(--wf-paper)] text-sm" data-testid="csa-review">
                      {[
                        ["Share", `${share.name} · ${share.items} items/week`],
                        ["Pickup", `${location?.name} · Thursdays`],
                        ["Payment", `${method?.label} · ${totals.label}`],
                        ["Member", `${details.name} · ${details.email}`],
                      ].map(([k, v]) => (
                        <div key={k} className="flex justify-between gap-4 px-5 py-3"><dt className="text-muted-foreground">{k}</dt><dd className="text-right font-medium text-[var(--wf-ink)]">{v}</dd></div>
                      ))}
                      {totals.fee > 0 && <div className="flex justify-between gap-4 px-5 py-3"><dt className="text-muted-foreground">Processing fee</dt><dd className="font-medium text-[var(--wf-ink)]">${totals.fee}</dd></div>}
                      <div className="flex justify-between gap-4 px-5 py-4"><dt className="font-semibold text-[var(--wf-ink)]">{payment === "snap" ? "Monthly Auto-SNAP charge" : installments ? "Due today" : "Total"}</dt><dd className="font-serif text-2xl font-semibold text-[var(--wf-maroon)]" data-testid="csa-total">${totals.due}</dd></div>
                    </dl>
                    {payment === "check" && (
                      <address className="mt-4 rounded-sm border border-[var(--wf-border)] bg-[var(--wf-paper)] p-4 text-sm not-italic leading-6 text-[var(--wf-soil)]">
                        {method.mailTo.map((l) => (<span key={l} className="block">{l}</span>))}
                      </address>
                    )}
                    <div className="mt-4 flex items-start gap-2">
                      <Checkbox id="csa-agree" checked={agree} onCheckedChange={(v) => setAgree(Boolean(v))} aria-invalid={!!errors.agree} data-testid="csa-agree-checkbox" className="mt-0.5 border-[var(--wf-border)] data-[state=checked]:bg-[var(--wf-maroon)] data-[state=checked]:border-[var(--wf-maroon)]" />
                      <Label htmlFor="csa-agree" className="text-sm leading-6 text-[var(--wf-soil)]">I understand this is a 16-week seasonal share and that World Farmers will contact me with pickup details.</Label>
                    </div>
                    {err("agree")}
                  </div>
                )}

                {/* Nav */}
                <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <button type="button" onClick={prevStep} disabled={step === 0} data-testid="csa-back-button" className="inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-[var(--wf-soil)] disabled:opacity-40 hover:text-[var(--wf-maroon)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"><ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back</button>
                  {step < STEPS.length - 1 ? (
                    <WfButton type="button" onClick={nextStep} withArrow className="px-7" data-testid="csa-next-button">Continue</WfButton>
                  ) : (
                    <WfButton type="button" onClick={confirm} withArrow className="px-7" data-testid="csa-confirm-button">{payment === "snap" ? "Request SNAP enrollment" : payment === "check" ? "Reserve & mail check" : "Complete sign-up"}</WfButton>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
        <p className="mx-auto mt-4 max-w-3xl text-center text-xs text-muted-foreground">Demo checkout — payment processing will be connected once a provider is selected.</p>
      </div>
    </section>
  );
};

/* ---------------- SNAP & HIP (information first) ---------------- */
const SnapSection = () => (
  <section id="snap-hip" aria-labelledby="csa-snap-heading" className="wf-section scroll-mt-20 bg-[var(--wf-farm-green)]">
    <div className="wf-container">
      <Reveal className="max-w-3xl">
        <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[rgba(255,253,248,0.85)]"><span aria-hidden="true" className="h-px w-6 bg-[rgba(255,253,248,0.5)]" />SNAP CSA</span>
        <h2 id="csa-snap-heading" className="mt-4 font-serif text-3xl font-semibold text-white sm:text-4xl lg:text-5xl">{CSA_SNAP_INFO.title}</h2>
        <p className="mt-4 text-base leading-7 text-[rgba(255,253,248,0.92)] sm:text-lg">{CSA_SNAP_INFO.intro}</p>
      </Reveal>

      <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Reveal className="lg:col-span-2">
          <div className="h-full border border-white/20 bg-white/10 p-6 backdrop-blur-sm sm:p-8">
            <h3 className="font-serif text-xl font-semibold text-white">How the SNAP/HIP CSA works</h3>
            <ol className="mt-4 space-y-3" data-testid="csa-snap-steps">
              {CSA_SNAP_INFO.points.map((p, i) => (
                <li key={p} className="flex items-start gap-3 text-sm leading-6 text-[rgba(255,253,248,0.94)] sm:text-base">
                  <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center bg-white/20 text-xs font-semibold text-white">{i + 1}</span>
                  {p}
                </li>
              ))}
            </ol>
          </div>
        </Reveal>
        <Reveal delay={0.08}>
          <div className="flex h-full flex-col gap-3">
            {CSA_SHARE_OPTIONS.map((s) => (
              <div key={s.id} className="border border-white/20 bg-white/10 p-5 backdrop-blur-sm" data-testid={`csa-snap-price-${s.id}`}>
                <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[var(--wf-wheat)]">{s.name}</p>
                <p className="mt-1 font-serif text-3xl font-semibold text-white">${s.snapMonthly}<span className="font-sans text-sm font-medium text-white/80"> / month</span></p>
                <p className="mt-1 text-xs text-white/85">4 monthly Auto-SNAP payments · July – October</p>
              </div>
            ))}
            <WfButton type="button" onClick={() => scrollToId("signup")} variant="light" withArrow className="mt-auto w-full" data-testid="csa-snap-signup-cta">Sign up with SNAP</WfButton>
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.1} className="mt-10">
        <h3 className="font-serif text-2xl font-semibold text-white">SNAP & HIP questions</h3>
        <Accordion type="single" collapsible className="mt-4 border border-white/20 bg-white/10 px-5 backdrop-blur-sm" data-testid="csa-snap-faq">
          {CSA_SNAP_INFO.faqs.map((f, i) => (
            <AccordionItem key={f.q} value={`snap-${i}`} className="border-white/20">
              <AccordionTrigger className="text-left font-semibold text-white hover:no-underline">{f.q}</AccordionTrigger>
              <AccordionContent className="text-sm leading-6 text-[rgba(255,253,248,0.92)] sm:text-base">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);

/* ---------------- FAQs (information first) ---------------- */
const FaqSection = () => (
  <section id="faqs" aria-labelledby="csa-faq-heading" className="wf-section scroll-mt-20 bg-[var(--wf-cream)]">
    <div className="wf-container">
      <Reveal className="max-w-3xl">
        <Kicker>CSA FAQs</Kicker>
        <h2 id="csa-faq-heading" className="mt-4 font-serif text-3xl font-semibold text-[var(--wf-ink)] sm:text-4xl lg:text-5xl">Everything you need to know about our CSA</h2>
        <p className="mt-4 text-base leading-7 text-[var(--wf-soil)]">What a CSA is, how sign-up works, what to expect each week, and how to pay.</p>
      </Reveal>
      <Reveal delay={0.08} className="mt-10">
        <Accordion type="single" collapsible className="border border-[var(--wf-border)] bg-[var(--wf-paper)] px-5 sm:px-8" data-testid="csa-faq">
          {CSA_FAQS.map((f, i) => (
            <AccordionItem key={f.q} value={`faq-${i}`} className="border-[var(--wf-border)]">
              <AccordionTrigger className="py-5 text-left font-serif text-lg font-semibold text-[var(--wf-ink)] hover:no-underline" data-testid={`csa-faq-trigger-${i}`}>{f.q}</AccordionTrigger>
              <AccordionContent className="max-w-[75ch] text-base leading-7 text-[var(--wf-soil)]">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
      <Reveal delay={0.1} className="mt-8">
        <div className="flex flex-col gap-3 border border-[var(--wf-border)] bg-[var(--wf-paper)] p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="csa-contact-strip">
          <p className="text-sm text-[var(--wf-soil)]">Still have questions? Contact our CSA Team.</p>
          <ul className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
            <li><a href={`mailto:${CSA_CONTACT.email}`} className="inline-flex items-center gap-2 font-semibold text-[var(--wf-maroon)] underline-offset-4 hover:underline" data-testid="csa-contact-email"><Mail className="h-4 w-4" aria-hidden="true" />{CSA_CONTACT.email}</a></li>
            <li><a href={CSA_CONTACT.phoneHref} className="inline-flex items-center gap-2 font-semibold text-[var(--wf-maroon)] underline-offset-4 hover:underline" data-testid="csa-contact-phone"><Phone className="h-4 w-4" aria-hidden="true" />{CSA_CONTACT.phone}</a></li>
          </ul>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------------- Page ---------------- */
export default function CsaPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [shareId, setShareId] = useState("");
  const choose = (id) => { setShareId(id); scrollToId("signup"); };
  return (
    <>
      <SkipToContent />
      <StickyHeader overlay linkToHome />
      <main id="content" tabIndex={-1} className="outline-none">
        <PageHero {...CSA_HERO} testid="csa-hero" />
        <QuickLinks />
        <SeasonStrip />
        <SharesSection onChoose={choose} />
        <PickupSection />
        <CsaSignup shareId={shareId} setShareId={setShareId} />
        <SeasonsSection />
        <SnapSection />
        <FaqSection />
      </main>
      <SiteFooter />
    </>
  );
}
